import { type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Accessibility,
  AlertTriangle,
  Bus,
  Car,
  ClipboardList,
  FileText,
  Link2,
  RefreshCw,
  ShieldCheck,
  UserCircle,
} from 'lucide-react';

import { DocValidadeBadge } from '@/modulos/operacao/comum/DocValidadeBadge';
import { ROTULO_DA_ORIGEM, type OrigemDoDado } from '@/modulos/operacao/comum/contrato';
import type { CondutorDetalhe } from '@/modulos/operacao/condutores/contrato';
import { useCondutor } from '@/modulos/operacao/condutores/dados';
import type { MonitorDetalhe } from '@/modulos/operacao/monitores/contrato';
import { useMonitor } from '@/modulos/operacao/monitores/dados';
import type { TitularDetalhe } from '@/modulos/operacao/titulares/contrato';
import { useTitular } from '@/modulos/operacao/titulares/dados';
import type { VeiculoDetalhe } from '@/modulos/operacao/veiculos/contrato';
import { useVeiculo } from '@/modulos/operacao/veiculos/dados';
import type { ErroApi } from '@/nucleo/auth/cliente-api';
import { formatarData } from '@/nucleo/formato/datas';
import {
  mascaraCep,
  mascaraCnpj,
  mascaraPlaca,
  mascaraTelefone,
} from '@/nucleo/formato/documentos';
import { DetailHeader } from '@/ui/DetailHeader';
import { DetailSummarySidebar } from '@/ui/DetailSummarySidebar';
import { DetailWithTimeline } from '@/ui/DetailWithTimeline';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { StatusBadge, type StatusVariant } from '@/ui/StatusBadge';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';

import { ROTULO_DO_ESTADO_DO_CADASTRO, type DocumentoDoRecurso, type OsDoRecurso } from './leitura';

/**
 * Detalhe de titular, veículo, condutor e monitor — `/operacao/{tipo}/:slug`.
 *
 * LÊ DA API (`GET bff/v1/{tipo}/:referencia`), sob o recorte do banco (0012): o que
 * o perfil não enxerga responde 404 e vira "não encontrado". A leitura do detalhe
 * fica registrada em `acessos_dado_pessoal`, e o CPF chega mascarado.
 *
 * O QUE A TELA MOSTRA, E DE ONDE:
 *   - os dados do cadastro, das tabelas da 0002 (onde a J-05 também escreve);
 *   - os documentos da J-05, com a validade aprovada pelo DTP — só para quem lê a
 *     documentação (papel global); para os outros a aba nem aparece;
 *   - as OS da jornada em que o recurso entrou — só para quem lê OS (0254).
 *
 * O QUE SAIU, E POR QUÊ: as abas Titularidade (histórico), Bancada e Auditoria do
 * veículo não têm leitura nesta rodada (bancada é regra de remuneração; a trilha
 * de auditoria não tem rota), e mostrar a estrutura com dado inventado afirmaria
 * o que ninguém apurou. O botão "Editar" também saiu: a edição de cadastro não
 * grava (só a J-05 muda recurso), e a tela de edição não tem mais de onde ler.
 */

export type TipoDeCadastro = 'condutores' | 'monitores' | 'veiculos' | 'titulares';

const ROTAS: Record<TipoDeCadastro, { lista: string; titulo: string; plural: string }> = {
  condutores: { lista: '/operacao/condutores', titulo: 'Condutor', plural: 'Condutores' },
  monitores: { lista: '/operacao/monitores', titulo: 'Monitor', plural: 'Monitores' },
  veiculos: { lista: '/operacao/veiculos', titulo: 'Veículo', plural: 'Veículos' },
  titulares: { lista: '/operacao/titulares', titulo: 'Titular', plural: 'Titulares' },
};

const ABAS_VEICULO = ['visao-geral', 'documentos', 'ordens'] as const;
type AbaVeiculo = (typeof ABAS_VEICULO)[number];

const ROTULO_DO_DOCUMENTO: Record<string, string> = {
  EM_ANALISE: 'Em análise',
  VALIDO: 'Válido',
  RECUSADO: 'Recusado',
  SUBSTITUIDO: 'Substituído',
};

const ROTULO_DA_VIGENCIA: Record<string, string> = {
  A_INICIAR: 'A iniciar',
  VIGENTE: 'Vigente',
  ENCERRADA: 'Encerrada',
};

function statusVariant(status: string | null | undefined): StatusVariant {
  const candidato = (status ?? '').toLowerCase() as StatusVariant;
  const validos: StatusVariant[] = ['ativo', 'inativo', 'suspenso', 'bloqueado', 'pendente'];
  return validos.includes(candidato) ? candidato : 'pendente';
}

function texto(valor: unknown): string {
  return valor == null || valor === '' ? '—' : String(valor);
}

function origem(valor: OrigemDoDado | null): string {
  return valor ? (ROTULO_DA_ORIGEM[valor] ?? valor) : '—';
}

function CampoLeitura({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}

function BlocoVazio({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <Card>
      <CardContent className="p-6">
        <EmptyState icon={Icon} title={title} description={description} />
      </CardContent>
    </Card>
  );
}

function Carregando() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Carregando detalhe">
      <div className="h-24 animate-pulse rounded-xl bg-muted" />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="h-96 animate-pulse rounded-xl bg-muted" />
        <div className="h-64 animate-pulse rounded-xl bg-muted" />
      </div>
    </div>
  );
}

function DocumentosTab({ documentos }: { documentos: DocumentoDoRecurso[] }) {
  if (documentos.length === 0) {
    return (
      <BlocoVazio
        icon={FileText}
        title="Nenhum documento enviado"
        description="Os documentos deste cadastro aparecem aqui quando forem enviados na jornada de recursos e analisados pelo DTP."
      />
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4 text-primary" aria-hidden="true" /> Documentos e validades
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          A última versão de cada documento. A validade é a aprovada na análise do DTP.
        </p>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border" aria-label="Documentos do cadastro">
          {documentos.map((documento) => (
            <li
              key={documento.id}
              className="flex flex-wrap items-center justify-between gap-2 py-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  {documento.titulo ?? documento.tipo_documento}
                </p>
                <p className="text-xs text-muted-foreground">
                  Versão {documento.versao} ·{' '}
                  {ROTULO_DO_DOCUMENTO[documento.status] ?? documento.status} · enviado em{' '}
                  {formatarData(documento.enviado_em)}
                </p>
              </div>
              {documento.status === 'VALIDO' ? (
                <DocValidadeBadge validade={documento.validade} emptyLabel="Sem validade" />
              ) : null}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function OrdensTab({ ordens, singular }: { ordens: OsDoRecurso[]; singular: string }) {
  if (ordens.length === 0) {
    return (
      <BlocoVazio
        icon={ClipboardList}
        title="Nenhuma OS vinculada"
        description={`Este ${singular} ainda não entrou em nenhuma Ordem de Serviço da jornada.`}
      />
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ClipboardList className="h-4 w-4 text-primary" aria-hidden="true" /> Ordens de Serviço
          vinculadas
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border" aria-label="Ordens de Serviço vinculadas">
          {ordens.map((ordem) => (
            <li key={ordem.id} className="py-2">
              <Link
                to={`/operacao/ordens-de-servico/${ordem.id}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md px-2 py-1 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="text-sm font-medium">{ordem.numero_completo ?? 'OS'}</span>
                <span className="text-xs text-muted-foreground">
                  {formatarData(ordem.inicio)} a {formatarData(ordem.fim)}
                </span>
                <Badge variant="outline">
                  {ordem.situacao_vigencia
                    ? (ROTULO_DA_VIGENCIA[ordem.situacao_vigencia] ?? ordem.situacao_vigencia)
                    : ordem.status}
                </Badge>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function VinculoTab({ registro }: { registro: CondutorDetalhe | MonitorDetalhe }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Link2 className="h-4 w-4 text-primary" aria-hidden="true" /> Vínculo atual
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
          <p className="text-xs text-muted-foreground">Credenciado</p>
          <p className="mt-1 font-medium">
            {registro.credenciados?.nome ?? 'Sem credenciado vinculado'}
          </p>
        </div>
        <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
          <p className="text-xs text-muted-foreground">Natureza do vínculo</p>
          <p className="mt-1 font-medium">{texto(registro.tipo_vinculo_empregatorio)}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function DadosDaPessoa({
  tipo,
  registro,
}: {
  tipo: 'condutores' | 'monitores';
  registro: CondutorDetalhe | MonitorDetalhe;
}) {
  const condutor = tipo === 'condutores' ? (registro as CondutorDetalhe) : null;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Dados pessoais</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <CampoLeitura label="CPF">
              <span className="font-mono">{texto(registro.cpf)}</span>
            </CampoLeitura>
            <CampoLeitura label="RG">{texto(registro.rg)}</CampoLeitura>
            <CampoLeitura label="Data de nascimento">
              {formatarData(registro.data_nascimento)}
            </CampoLeitura>
            <CampoLeitura label="Situação">{texto(registro.status)}</CampoLeitura>
            <CampoLeitura label="Cadastro">
              {registro.estado_cadastro
                ? (ROTULO_DO_ESTADO_DO_CADASTRO[registro.estado_cadastro] ??
                  registro.estado_cadastro)
                : '—'}
            </CampoLeitura>
            <CampoLeitura label="Origem do dado">{origem(registro.origem_dado)}</CampoLeitura>
          </dl>
          {registro.observacoes ? (
            <p className="mt-4 whitespace-pre-wrap border-t border-border pt-4 text-sm">
              {registro.observacoes}
            </p>
          ) : null}
        </CardContent>
      </Card>
      {condutor && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" /> Habilitação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <CampoLeitura label="CNH — número">{texto(condutor.cnh_numero)}</CampoLeitura>
              <CampoLeitura label="CNH — categoria">{texto(condutor.cnh_categoria)}</CampoLeitura>
              <CampoLeitura label="CRMC — número">{texto(condutor.crmc_numero)}</CampoLeitura>
              <CampoLeitura label="Preposto">{condutor.eh_preposto ? 'Sim' : 'Não'}</CampoLeitura>
            </dl>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DadosDoVeiculo({ registro }: { registro: VeiculoDetalhe }) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bus className="h-4 w-4 text-primary" aria-hidden="true" /> Identificação do veículo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <CampoLeitura label="Placa">
              <span className="font-mono">{mascaraPlaca(registro.placa ?? '')}</span>
            </CampoLeitura>
            <CampoLeitura label="CRM">{texto(registro.crm)}</CampoLeitura>
            <CampoLeitura label="Marca">{texto(registro.marca)}</CampoLeitura>
            <CampoLeitura label="Modelo">{texto(registro.modelo)}</CampoLeitura>
            <CampoLeitura label="Ano fabricação/modelo">
              {texto(registro.ano_fabricacao)} / {texto(registro.ano_modelo)}
            </CampoLeitura>
            <CampoLeitura label="Cor">{texto(registro.cor)}</CampoLeitura>
            <CampoLeitura label="Combustível">{texto(registro.tipo_combustivel)}</CampoLeitura>
            <CampoLeitura label="Chassi">{texto(registro.chassi)}</CampoLeitura>
            <CampoLeitura label="RENAVAM">{texto(registro.renavam)}</CampoLeitura>
            <CampoLeitura label="Cadastro">
              {registro.estado_cadastro
                ? (ROTULO_DO_ESTADO_DO_CADASTRO[registro.estado_cadastro] ??
                  registro.estado_cadastro)
                : '—'}
            </CampoLeitura>
            <CampoLeitura label="Seguradora">{texto(registro.seguro_seguradora)}</CampoLeitura>
            <CampoLeitura label="Titular atual">
              {registro.titular_atual ? (
                <Link
                  to={`/operacao/titulares/${registro.titular_atual.slug}`}
                  className="text-link underline-offset-2 hover:underline"
                >
                  {texto(registro.titular_atual.nome)}
                </Link>
              ) : (
                '—'
              )}
            </CampoLeitura>
          </dl>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Accessibility className="h-4 w-4 text-primary" aria-hidden="true" /> Capacidades e
            acessibilidade
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {(
            [
              ['DETRAN', registro.capacidade_detran],
              ['TEG', registro.capacidade_teg],
              ['Creche', registro.capacidade_creche],
              ['Ancoragens', registro.capacidade_acessivel],
            ] as Array<[string, unknown]>
          ).map(([label, value]) => (
            <Badge key={label} variant="outline" className="px-3 py-1.5 text-sm">
              <span className="mr-1 text-muted-foreground">{label}</span>
              <span className="font-semibold">{texto(value)}</span>
            </Badge>
          ))}
          {registro.acessivel === true && (
            <Badge variant="outline">
              <Accessibility className="mr-1 h-3 w-3" aria-hidden="true" /> Acessível
            </Badge>
          )}
          {registro.acessivel === true && !registro.capacidade_acessivel && (
            <Badge variant="outline" className="border-warning text-warning-text">
              <AlertTriangle className="mr-1 h-3 w-3" aria-hidden="true" /> Ancoragens pendentes
            </Badge>
          )}
        </CardContent>
      </Card>
      {registro.motivo_inativacao ? (
        <Alert className="border-warning/40 bg-warning/5">
          <AlertTriangle className="h-4 w-4 text-warning" aria-hidden="true" />
          <AlertDescription>
            Inativado por regra
            {registro.tag_origem_inativacao ? ` (${registro.tag_origem_inativacao})` : ''}:{' '}
            {registro.motivo_inativacao}
          </AlertDescription>
        </Alert>
      ) : null}
      {registro.observacoes ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Observações</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm">{registro.observacoes}</p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function TitularDetalheConteudo({ registro }: { registro: TitularDetalhe }) {
  const endereco = [
    registro.endereco,
    registro.numero,
    registro.complemento,
    registro.bairro,
    registro.cidade && registro.uf ? `${registro.cidade}/${registro.uf}` : registro.cidade,
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <div className="space-y-4">
      <Alert className="border-primary/30 bg-primary/5">
        <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />
        <AlertDescription>
          Titularidade é somente consulta: o registro é recebido do SGTP e não possui formulário de
          edição neste módulo.
        </AlertDescription>
      </Alert>
      {registro.conflito_integracao && (
        <Alert className="border-warning/40 bg-warning/5">
          <AlertTriangle className="h-4 w-4 text-warning" aria-hidden="true" />
          <AlertDescription>
            {registro.detalhe_conflito ??
              'O SGTP discorda deste cadastro e a decisão ainda não foi registrada.'}
          </AlertDescription>
        </Alert>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserCircle className="h-4 w-4 text-primary" aria-hidden="true" /> Identificação
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <CampoLeitura label="Nome">{texto(registro.nome)}</CampoLeitura>
            <CampoLeitura label={registro.cpf ? 'CPF' : 'CNPJ'}>
              <span className="font-mono">
                {registro.cpf ?? (registro.cnpj ? mascaraCnpj(registro.cnpj) : '—')}
              </span>
            </CampoLeitura>
            <CampoLeitura label="Natureza">
              {registro.cpf ? 'Pessoa física' : registro.cnpj ? 'Pessoa jurídica' : '—'}
            </CampoLeitura>
            {registro.cpf ? (
              <>
                <CampoLeitura label="RG">{texto(registro.rg)}</CampoLeitura>
                <CampoLeitura label="Data de nascimento">
                  {formatarData(registro.data_nascimento)}
                </CampoLeitura>
              </>
            ) : null}
            <CampoLeitura label="CRMC">
              {texto(registro.crmc_numero)}
              {registro.crmc_validade ? ` · até ${formatarData(registro.crmc_validade)}` : ''}
            </CampoLeitura>
            <CampoLeitura label="Telefone">
              {registro.telefone ? mascaraTelefone(registro.telefone) : '—'}
            </CampoLeitura>
            <CampoLeitura label="E-mail">{texto(registro.email)}</CampoLeitura>
            <CampoLeitura label="Endereço">
              {endereco || '—'}
              {registro.cep ? ` · CEP ${mascaraCep(registro.cep)}` : ''}
            </CampoLeitura>
            <CampoLeitura label="Origem">{origem(registro.origem_dado)}</CampoLeitura>
            <CampoLeitura label="Referência no SGTP">
              {texto(registro.referencia_integracao)}
            </CampoLeitura>
          </dl>
        </CardContent>
      </Card>
      {registro.veiculos.length === 0 ? (
        <BlocoVazio
          icon={Car}
          title="Nenhum veículo vinculado"
          description="Nenhum veículo deste titular está no seu recorte de consulta."
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Car className="h-4 w-4 text-primary" aria-hidden="true" /> Veículos vinculados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border" aria-label="Veículos vinculados">
              {registro.veiculos.map((vinculo) => (
                <li key={vinculo.id} className="py-2">
                  <Link
                    to={`/operacao/veiculos/${vinculo.veiculo.slug}`}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md px-2 py-1 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="font-mono text-sm font-medium">
                      {mascaraPlaca(vinculo.veiculo.placa ?? '')}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {texto(vinculo.veiculo.modelo)} · desde {formatarData(vinculo.data_inicio)}
                      {vinculo.data_fim ? ` até ${formatarData(vinculo.data_fim)}` : ''}
                    </span>
                    <Badge variant="outline">{vinculo.data_fim ? 'Encerrado' : 'Vigente'}</Badge>
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

type Registro =
  | { tipo: 'veiculos'; dado: VeiculoDetalhe }
  | { tipo: 'condutores'; dado: CondutorDetalhe }
  | { tipo: 'monitores'; dado: MonitorDetalhe }
  | { tipo: 'titulares'; dado: TitularDetalhe };

/** Os quatro ganchos são chamados sempre; só o do tipo da rota busca (os outros ficam sem slug). */
function useRegistro(tipo: TipoDeCadastro, slug: string | undefined) {
  const veiculo = useVeiculo(tipo === 'veiculos' ? slug : undefined);
  const condutor = useCondutor(tipo === 'condutores' ? slug : undefined);
  const monitor = useMonitor(tipo === 'monitores' ? slug : undefined);
  const titular = useTitular(tipo === 'titulares' ? slug : undefined);
  const consulta = {
    veiculos: veiculo,
    condutores: condutor,
    monitores: monitor,
    titulares: titular,
  }[tipo];
  const dado = consulta.data;
  const registro = dado ? ({ tipo, dado } as Registro) : null;
  return {
    carregando: consulta.isLoading,
    erro: consulta.isError ? (consulta.error as ErroApi | null) : null,
    naoEncontrado: consulta.isSuccess && dado === null,
    registro,
    tentarDeNovo: () => void consulta.refetch(),
  };
}

export function CadastroDetalhePage({ tipo }: { tipo: TipoDeCadastro }) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rotas = ROTAS[tipo];
  const { carregando, erro, naoEncontrado, registro, tentarDeNovo } = useRegistro(tipo, slug);

  if (carregando) return <Carregando />;

  if (erro) {
    return (
      <ErrorState
        variant="load-failed"
        description={
          erro.aviso ?? 'Não foi possível carregar o detalhe deste cadastro. Tente novamente.'
        }
        primaryAction={{ label: 'Tentar de novo', onClick: tentarDeNovo }}
      />
    );
  }

  if (naoEncontrado || !registro) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title={`${rotas.titulo} não encontrado`}
        description="O registro não existe ou não está no seu recorte de consulta. Volte para a listagem e escolha uma linha."
        actionLabel={`Voltar para ${rotas.plural.toLowerCase()}`}
        onAction={() => navigate(rotas.lista)}
      />
    );
  }

  const dado = registro.dado;
  const ehVeiculo = registro.tipo === 'veiculos';
  const nome =
    registro.tipo === 'veiculos'
      ? mascaraPlaca(registro.dado.placa ?? '')
      : String(registro.dado.nome ?? '');
  const subtitulo =
    registro.tipo === 'veiculos'
      ? [registro.dado.marca, registro.dado.modelo].filter(Boolean).join(' • ') ||
        'Cadastro de veículo'
      : `${rotas.titulo} do programa TEG`;
  const credenciado =
    registro.tipo === 'titulares' ? null : (registro.dado.credenciados?.nome ?? null);

  const abaVeiculo: AbaVeiculo = ABAS_VEICULO.includes(searchParams.get('tab') as AbaVeiculo)
    ? (searchParams.get('tab') as AbaVeiculo)
    : 'visao-geral';

  const aside = (
    <DetailSummarySidebar
      items={[
        {
          label: ehVeiculo ? 'Placa' : 'Nome',
          value: ehVeiculo ? <span className="font-mono">{nome}</span> : texto(nome),
        },
        { label: 'Situação', value: texto(dado.status) },
        registro.tipo === 'condutores' || registro.tipo === 'monitores'
          ? { label: 'CPF', value: <span className="font-mono">{texto(registro.dado.cpf)}</span> }
          : false,
        registro.tipo === 'veiculos' ? { label: 'CRM', value: texto(registro.dado.crm) } : false,
        credenciado ? { label: 'Credenciado', value: credenciado } : false,
        { label: 'Origem', value: origem(dado.origem_dado) },
      ]}
      audit={{ createdAt: String(dado.created_at ?? ''), updatedAt: String(dado.updated_at ?? '') }}
    />
  );

  const summary = (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className={`text-xl font-bold ${ehVeiculo ? 'font-mono' : ''}`}>{nome}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{subtitulo}</p>
            {credenciado && (
              <p className="mt-1 text-sm text-muted-foreground">
                Credenciado: <span className="font-medium text-foreground">{credenciado}</span>
              </p>
            )}
          </div>
          <StatusBadge status={statusVariant(dado.status)} label={texto(dado.status)} />
        </div>
      </CardContent>
    </Card>
  );

  let conteudo: ReactNode;
  if (registro.tipo === 'veiculos') {
    const { documentos, ordens } = registro.dado;
    conteudo = (
      <Tabs
        value={abaVeiculo}
        onValueChange={(aba) => {
          const params = new URLSearchParams(searchParams);
          params.set('tab', aba);
          setSearchParams(params, { replace: true });
        }}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="visao-geral">Visão geral</TabsTrigger>
          {documentos && <TabsTrigger value="documentos">Documentos</TabsTrigger>}
          {ordens && <TabsTrigger value="ordens">Ordens de Serviço</TabsTrigger>}
        </TabsList>
        <TabsContent value="visao-geral">
          <DadosDoVeiculo registro={registro.dado} />
        </TabsContent>
        {documentos && (
          <TabsContent value="documentos">
            <DocumentosTab documentos={documentos} />
          </TabsContent>
        )}
        {ordens && (
          <TabsContent value="ordens">
            <OrdensTab ordens={ordens} singular="veículo" />
          </TabsContent>
        )}
      </Tabs>
    );
  } else if (registro.tipo === 'titulares') {
    conteudo = <TitularDetalheConteudo registro={registro.dado} />;
  } else {
    const { documentos, ordens } = registro.dado;
    conteudo = (
      <Tabs defaultValue="dados" className="space-y-4">
        <TabsList>
          <TabsTrigger value="dados">Dados pessoais</TabsTrigger>
          {documentos && <TabsTrigger value="documentos">Documentos</TabsTrigger>}
          <TabsTrigger value="vinculos">Vínculo</TabsTrigger>
          {ordens && <TabsTrigger value="os">OS vinculadas</TabsTrigger>}
        </TabsList>
        <TabsContent value="dados">
          <DadosDaPessoa tipo={registro.tipo} registro={registro.dado} />
        </TabsContent>
        {documentos && (
          <TabsContent value="documentos">
            <DocumentosTab documentos={documentos} />
          </TabsContent>
        )}
        <TabsContent value="vinculos">
          <VinculoTab registro={registro.dado} />
        </TabsContent>
        {ordens && (
          <TabsContent value="os">
            <OrdensTab
              ordens={ordens}
              singular={registro.tipo === 'condutores' ? 'condutor' : 'monitor'}
            />
          </TabsContent>
        )}
      </Tabs>
    );
  }

  return (
    <div className="space-y-4">
      <DetailHeader
        backTo={rotas.lista}
        backLabel={rotas.plural}
        title={nome}
        subtitle={subtitulo}
        status={{ variant: statusVariant(dado.status), label: texto(dado.status) }}
        actions={
          registro.tipo === 'titulares' ? (
            <Button variant="outline" onClick={() => navigate('/admin/integracoes')}>
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" /> Acompanhar integração
            </Button>
          ) : undefined
        }
      />
      <DetailWithTimeline timeline={aside} summary={summary}>
        {conteudo}
      </DetailWithTimeline>
    </div>
  );
}

export default CadastroDetalhePage;
